import dlv from 'dlv';
import { klona } from 'klona';
import { deepEqual } from 'fast-equals';

/**
 * A plain, string-keyed object of unknown values.
 *
 * Used as the structural input/output type for the object helpers in this
 * module. Values are intentionally `unknown` so callers must narrow before use.
 */
type NestedObject = Record<string, unknown>;

/**
 * Type guard for a "plain" nested object worth recursing into.
 *
 * Returns `true` for non-`null`, non-array objects. Note that it also returns
 * `true` for `Date`, `RegExp`, `Map`, class instances, etc. (anything whose
 * `typeof` is `'object'`); only `null` and arrays are excluded.
 *
 * @param value - The value to test.
 * @returns `true` if `value` is a non-null, non-array object.
 */
const isNestedObject = (value: unknown): value is NestedObject => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/**
 * Deep-clones a value, returning a structurally independent copy.
 *
 * Re-exported from {@link https://github.com/lukeed/klona | klona}: handles
 * Objects, Arrays, `Date`, `RegExp`, `Map`, `Set`, and more. The return type is
 * the same as the input type.
 *
 * @example
 * ```ts
 * const copy = objClone(original); // deep copy, original untouched
 * ```
 */
const objClone = klona;

/**
 * Recursively flattens a nested object into a single-level object.
 *
 * Nested keys are concatenated onto their parent prefix with the child key's
 * first letter upper-cased, e.g. `{ a: { b: 1 } }` becomes `{ aB: 1 }`.
 *
 * Leaf values (anything that is not a plain nested object per
 * {@link isNestedObject}) are copied as-is. In particular **arrays and `null`
 * are treated as leaves** and are not recursed into.
 *
 * @param obj - The object to flatten.
 * @param prefix - Key prefix to prepend to every produced key. Defaults to `''`
 *   and is primarily used internally during recursion.
 * @returns A new, single-level object. The input is not mutated.
 *
 * @example
 * ```ts
 * objFlatten({ air: { inlet: 20 }, fan: { speed: 100 } });
 * // => { airInlet: 20, fanSpeed: 100 }
 * ```
 */
const objFlatten = (obj: NestedObject, prefix: string = ''): NestedObject =>
  Object.keys(obj).reduce((acc, k) => {
    const key = prefix.length ? `${prefix}${k.charAt(0).toUpperCase()}${k.slice(1)}` : k;
    const value = obj[k];

    if (isNestedObject(value)) {
      Object.assign(acc, objFlatten(value, key));
    } else {
      acc[key] = value;
    }

    return acc;
  }, {});

/**
 * Safely reads a dot-notated path from a nested object.
 *
 * Re-exported from {@link https://github.com/lukeed/dlv | dlv}: returns the
 * optional default when the path does not exist or resolves to `undefined`,
 * instead of throwing on missing intermediate keys.
 *
 * @param obj - The object to read from.
 * @param keypath - Dot-notated path (or array of keys), e.g. `'a.b.c'`.
 * @param defaultValue - Value returned when the path is missing/`undefined`.
 *
 * @example
 * ```ts
 * objSafeGet({ a: { b: 1 } }, 'a.b');        // => 1
 * objSafeGet({ a: {} }, 'a.b.c', 'fallback'); // => 'fallback'
 * ```
 */
const objSafeGet = dlv;

/**
 * Converts the object's string values to numbers, **mutating it in place**.
 *
 * For every own key whose value is a `string`, the value is replaced with
 * `parseFloat(value)`, or `null` when the string is empty (or otherwise
 * falsy). Non-string values are left untouched.
 *
 * @param obj - The object to format. Mutated in place.
 * @returns Nothing — the input object is modified directly.
 *
 * @example
 * ```ts
 * const data = { temp: '20.5', name: 'A', blank: '' };
 * formatObjectValues(data);
 * // data is now { temp: 20.5, name: 'A', blank: null }
 * ```
 */
const formatObjectValues = (obj: NestedObject): void =>
  Object.keys(obj).forEach((k) => {
    const value = obj[k];
    if (typeof value === 'string') {
      obj[k] = value ? parseFloat(value) : null;
    }
  });

/**
 * Deep structural equality check.
 *
 * Re-exported from {@link https://github.com/planttheidea/fast-equals | fast-equals}:
 * compares two values recursively and returns `true` when they are deeply
 * equal.
 *
 * @example
 * ```ts
 * objDeepEqual({ a: [1, 2] }, { a: [1, 2] }); // => true
 * objDeepEqual({ a: 1 }, { a: 2 });           // => false
 * ```
 */
const objDeepEqual = deepEqual;

export { objClone, objFlatten, objSafeGet, formatObjectValues, objDeepEqual };
